<template>
  <div
    :class="[fullwidth ? (lgFullwidth ? 'w-full' : 'w-auto') : '', 'relative']"
  >
    <div
      :class="[
        inset
          ? !!invalid
            ? 'field-inset-invalid w-full'
            : 'field-inset-default w-full'
          : '',
        'relative h-full',
        disabled && inset ? 'bg-gray-100' : null,
      ]"
    >
      <label
        v-if="label"
        fullwidth
        :for="id"
        class="block text-left text-gray-700"
        :class="[inset ? 'text-xs font-bold' : 'mb-2 text-sm font-medium']"
        >{{ label }}
        <span class="tooltip text-red-600" v-if="required">
          *
          <span class="tooltiptext" style="width: 120px">
            Kolom Wajib Diisi
          </span>
        </span>
      </label>
      <slot>
        <div
          v-if="type === 'search'"
          :class="
            inset
              ? 'absolute top-0 right-0 flex h-full items-center pr-3'
              : 'absolute flex h-full items-center pl-3'
          "
        >
          <Icon icon="heroicons:magnifying-glass" />
        </div>
        <textarea
          v-if="tag === 'textarea'"
          v-bind="props"
          v-model="inputVal"
          v-on:change="handleChange"
          v-on:input="handleInput"
          v-on:focus="handleFocus"
          v-on:blur="handleBlur"
        />
        <input
          v-else
          ref="input"
          :type="forceType ?? type"
          :accept="accept"
          :readonly="readonly"
          :multiple="multiple"
          v-bind="props"
          v-model="inputVal"
          v-on:change="handleChange"
          v-on:focus="handleFocus"
          v-on:input="handleInput"
          v-on:blur="handleBlur"
          v-on:focusout="$emit('focus-out')"
        />
        <slot name="next-input" />
      </slot>
    </div>
    <p class="mt-2 text-sm text-red-600" v-if="!!invalid">{{ message }}</p>
    <slot name="append" />
    <button
      v-if="type === 'password'"
      type="button"
      :style="{ top: label && !inset ? '2.4rem' : '' }"
      :class="label && !inset ? 'absolute right-0 pr-3' : 'absolute top-0 right-0 flex h-full items-center pr-3 text-gray-900'"
      @click="onViewPassword"
    >
      <Icon
        :icon="
          forceType === 'text'
            ? 'heroicons:eye-slash-solid'
            : 'heroicons:eye-solid'
        "
        class="h-5 w-5"
      />
    </button>
  </div>
</template>

<script>
import debounce from 'debounce';
import { inputCurrency } from '@/services/currency.service.js';
import dayjs from 'dayjs';

export default {
  name: 'BaseInput',
  props: {
    tag: {
      type: String,
      default: 'input',
    },
    type: {
      type: String,
      default: 'input',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    multiple: Boolean,
    withLabel: {
      type: Boolean,
      default: false,
    },
    inset: {
      type: Boolean,
      default: false,
    },
    shadow: {
      type: Boolean,
      default: true,
    },
    fullwidth: {
      type: Boolean,
      default: false,
    },
    lgFullwidth: {
      type: Boolean,
      default: true,
    },
    xlFullwidth: {
      type: Boolean,
      default: true,
    },
    autofocus: {
      type: Boolean,
      default: false,
    },
    invalid: Boolean,
    value: null,
    label: String,
    placeholder: String,
    min: String,
    max: String,
    message: String,
    id: String,
    autocomplete: String,
    rows: String,
    size: {
      type: String,
      default: 'md',
    },
    classes: {
      type: Object,
      default: () => ({}),
    },
    debounce: {
      type: Boolean,
      default: false,
    },
    currency: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
    accept: String,
    search: {
      type: Boolean,
      default: false,
    },
    forceLeft: Boolean,
    decimal: Boolean
  },
  emits: ['input', 'native-input', 'change', 'focus', 'focus-out'],
  computed: {
    inputVal: {
      get: function () {
        if (this.type == 'date') {
          return dayjs(this.value).format('YYYY-MM-DD');
        }
        if (this.currency) {
          // input type="text", kalo number nnt error
          const val = inputCurrency(this.value);
          return val;
        }

        if (this.decimal) {
          if (this.value === null) {
            return ''
          }
          
          let txt = this.value.toString().replace(/[^0-9.]/g, '');

          const parts = txt.split('.')
          
          if (parts.length > 2) {
            txt = parts[0] + '.' + parts[1]
          }

          return txt
        }

        return this.value;
      },
      set: function (value) {
        this.$emit('input', value);
      },
    },
    props: function () {
      const sizes = {
        sm: 'h-8 px-2',
        md: this.tag === 'textarea' ? 'min-h-10' : 'h-10',
        'padding-md': 'p-2',
      };

      return {
        class: [
          this.forceLeft ? 'text-left' : '',
          'block bg-white sm:text-sm',
          this.inset
            ? 'w-full border-0 px-0 pb-0 mt-1 pt-0 text-gray-900 placeholder-gray-200 focus:ring-0 sm:text-sm'
            : 'rounded-md border px-2',
          this.shadow ? (this.inset ? '' : 'shadow') : '',
          this.fullwidth
            ? this.lgFullwidth
              ? 'w-full'
              : this.xlFullwidth ? 'w-full lg:w-auto' : 'w-full xl:w-auto'
            : '',
          this.invalid
            ? 'border-red-300 placeholder-red-300 focus:border-red-500 focus:ring-red-500'
            : 'border-gray-300 focus:border-green-500 focus:ring-green-500',
          this.disabled ? 'bg-gray-100' : '',
          this.inset ? '' : sizes[this.size],
          this.classes.input,
          this.type === 'search' ? (this.inset ? '' : 'pl-10') : '',
        ],
        placeholder: this.placeholder,
        min: this.min,
        max: this.max,
        id: this.id,
        autocomplete: this.autocomplete,
        rows: this.rows,
        disabled: this.disabled,
        required: this.required,
      };
    },
  },
  data() {
    return {
      forceType: null,
    };
  },
  methods: {
    debounceEmitNativeInput: debounce(function () {
      this.$emit('native-input');
    }, 500),
    handleChange: function (e) {
      e.preventDefault()
      this.$emit('change', e);
    },
    handleInput: function (e) {
      if (this.debounce) {
        this.debounceEmitNativeInput();
      } else {
        this.$emit('native-input', e.target.value);
      }
    },
    handleFocus: function () {
      this.$emit('focus');
    },
    handleBlur: function (e) {
      this.$emit('blur', e.target.value);
    },
    onViewPassword() {
      if (this.forceType === 'text') {
        this.forceType = null;
      } else {
        this.forceType = 'text';
      }
    },
  },
  mounted() {
    if (this.autofocus) {
      this.$refs.input.focus();
    }
  },
};
</script>
